<template>
  <VueDatePicker v-bind="$attrs" ref="datepicker">
    <template #month-year="{ month, year, handleMonthYearChange }">
      <div class="title-month">
        <div>{{ dayjs().month(month).format('MMMM') }}</div>
        <div>{{ year }}</div>
      </div>
      <div class="navigation-button">
        <SPAButton
          class="custom-icon btn-prev-month"
          color="primary"
          @click="handleMonthYearChange(false)"
        >
          <CaretLeft />
        </SPAButton>
        <SPAButton
          class="custom-icon btn-next-month"
          color="primary"
          @click="handleMonthYearChange(true)"
        >
          <CaretRight />
        </SPAButton>
      </div>
    </template>

    <!-- Calendar Header -->
    <template #calendar-header="{ index }">
      <div class="tw-text-[10px] tw-uppercase tw-text-neutral-800">
        {{ dayjs().day(index).format('ddd') }}
      </div>
    </template>

    <template #day="{ day, date }">
      <div v-if="dayjs(date).day() === 0 || dayjs(date).day() === 6">
        {{ day }}
      </div>
      <div v-else>
        {{ day }}
      </div>

      <span
        v-if="holidays.includes(getformatedDate(date))"
        class="tw-absolute tw-bottom-[2px]"
      >
        <img
          class="holiday-calendar-icon"
          src="@/assets/images/2024/calendar-icon.svg"
          alt="holiday-calendar-icon"
        />
      </span>
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import CaretRight from '~icons/ph/caret-right';
import CaretLeft from '~icons/ph/caret-left';
import SPAButton from '@/components/SPAButton.vue';
import { getformatedDate } from '@/helpers/dateParser';
import type { DatePickerInstance } from '@vuepic/vue-datepicker';
import { ref, onMounted } from 'vue';

defineProps({
  holidays: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['onSetRefCalendar']);
const datepicker = ref<DatePickerInstance | null>(null);

onMounted(() => {
  emit('onSetRefCalendar', datepicker.value);
});
</script>

<style lang="scss">
.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: none;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #1976d2;
  --dp-primary-disabled-color: #6bacea;
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: #ddd;
  --dp-menu-border-color: #ddd;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
}

.dp__main,
.dp__theme_light,
.dp__flex_display {
  @apply tw-h-full;
}

.dp__main > div:nth-child(1) {
  @apply tw-hidden #{!important};
}

.dp__menu > div:nth-child(2) {
  @apply tw-h-full #{!important};
}

.dp__main > div:nth-child(2) {
  @apply tw-flex tw-h-full tw-flex-1 tw-self-stretch;
}

.dp__main > div {
  @apply tw-flex tw-flex-1 tw-self-stretch;
}

.dp__outer_menu_wrap {
  @apply tw-h-full tw-flex-1;
}

.dp__menu {
  @apply tw-h-full;

  > div:nth-child(1) {
    @apply tw-h-full;
  }

  .dp-menu-loading {
    @apply tw-z-[1] #{!important};
  }

  .dp--menu-loader {
    @apply tw-border-green-100 tw-border-b-transparent;
  }
}

.dp__instance_calendar {
  @apply tw-h-full;
}

.dp__menu_inner {
  @apply tw-h-full tw-flex-1 tw-p-0;

  > div:nth-child(1) {
    @apply tw-relative tw-flex tw-h-full tw-flex-col;
  }

  .dp--header-wrap {
    box-shadow: 0px -0.5px 0px 0px rgba(0, 0, 0, 0.3) inset;
    @apply tw-flex tw-flex-1 tw-gap-[10px];
  }

  .dp__month_year_wrap {
    @apply tw-flex tw-items-center tw-justify-between tw-self-stretch tw-p-3;

    .title-month {
      @apply tw-flex tw-items-center tw-justify-start tw-gap-[10px] tw-text-base tw-font-medium tw-text-neutral-300;
    }

    .navigation-button {
      @apply tw-flex;

      button {
        margin: 0;
        @apply tw-m-0;
      }
    }
  }

  //Calendar Styles
  .dp__calendar {
    @apply tw-h-full tw-p-0;

    .dp__calendar_header {
      @apply tw-flex tw-h-[48px] tw-justify-center;
    }

    .dp__calendar_header_item {
      @apply tw-flex tw-w-full tw-flex-1 tw-items-center tw-justify-center;
    }

    .dp__calendar_header_separator {
      @apply tw-hidden tw-h-0;
    }

    .dp__calendar {
      @apply tw-flex tw-h-full tw-flex-1 tw-flex-col tw-items-start tw-self-stretch;

      .dp__calendar_row {
        @apply tw-m-0 tw-flex tw-flex-1 tw-items-start tw-self-stretch;

        .dp__calendar_item {
          @apply tw-relative tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-self-stretch;
        }
      }
    }
  }

  .dp__calendar > div:nth-child(1) {
    @apply tw-flex tw-h-full tw-flex-col;
  }
  .dp__calendar_row {
    @apply tw-flex tw-flex-row #{!important};
  }
}
.dp__input {
  &:focus {
    @apply tw-border-2 tw-border-solid tw-border-green-300 tw-outline-green-300 #{!important};
  }
}

.dp__cell_inner {
  @apply tw-h-full tw-w-full tw-rounded-none tw-p-0 tw-text-neutral-700;
  &.dp__pointer {
    &.dp__today,
    &.dp--future,
    &.dp--past {
      @apply tw-border tw-border-neutral-20;

      &.dp__range_start,
      &.dp__range_end {
        @apply tw-bg-green-300 tw-font-medium tw-text-neutral-0;
      }

      &.dp__range_between {
        @apply tw-bg-green-50;
      }
    }

    &.dp__today.dp__active_date {
      @apply tw-text-neutral-0;
    }
  }

  &.dp__cell_disabled {
    @apply tw-text-neutral-40;
    &.dp--future,
    &.dp--past,
    &.dp__today {
      &.dp__range_between {
        @apply tw-bg-green-50;
      }
    }
  }

  &.dp__cell_offset.dp__pointer {
    &.dp__range_end {
      @apply tw-bg-green-300;
    }

    &.dp__range_between {
      @apply tw-bg-green-50;
    }
  }

  &.dp__range_start,
  &.dp__range_end {
    @apply tw-bg-green-300 tw-font-medium tw-text-neutral-0;
  }

  &.dp__range_between {
    @apply tw-bg-green-50;
  }

  &.dp__cell_disabled.dp__today,
  &.dp__pointer.dp__today {
    @apply tw-border-none tw-font-medium tw-text-green-300;
  }
}

.dp__menu_disabled {
  z-index: -1;
}

.dp__calendar_item:first-child,
.dp__calendar_item:last-child {
  @apply tw-bg-neutral-10;
}

.dp__marker_dot {
  @apply tw-my-[5px] tw-bg-transparent;
}

.dp__calendar_header_item {
  @apply tw-content-center;
}

.dp__today {
  @apply tw-border-none;
}

.dp__active_date {
  @apply tw-bg-green-300 tw-font-medium;
}

.dp__arrow_bottom_tp {
  @apply tw-bg-neutral-900;
}

.dp__calendar_header_separator {
  @apply tw-h-0 tw-bg-none;
}

.dp__tooltip_text {
  @apply tw-max-w-56 tw-text-wrap tw-text-left tw-text-sm tw-text-neutral-0;
}

.dp__marker_tooltip {
  @apply tw-bg-neutral-900 tw-text-neutral-0;
  @apply tw-w-[100px];
}

.dp__tooltip_mark {
  @apply tw-hidden tw-bg-transparent #{!important};
}

</style>
